import { Alert, Box, Tab, Tabs, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStore } from '../stores/sessionStore.js'
import { ReglagesAgenda } from '../features/stores/ReglagesAgenda.js'
import { EditeurSite } from '../features/site/EditeurSite.js'
import { ReglagesWhatsApp } from '../features/campagnes/ReglagesWhatsApp.js'
import { SyncSettingsPage } from './SyncSettingsPage.js'

/**
 * Le module Paramètres de la boutique.
 *
 * ⭐ Un sommaire à onglets plutôt qu'une page unique : ces réglages n'ont rien
 * à voir entre eux — ce que l'appareil détient hors ligne, les jours
 * d'ouverture, la vitrine — et se consultent séparément.
 *
 * ⚠️ L'onglet est dans l'ADRESSE, pas dans un état local. `Paramètres ›
 * Synchronisation` est cité dans les messages d'erreur de synchronisation et
 * se trouve dans les favoris : un onglet purement local aurait renvoyé ces
 * liens sur le premier onglet venu, sans rien dire.
 */
const ONGLETS = [
  { code: 'synchronisation', label: 'Synchronisation' },
  { code: 'agenda', label: 'Agenda' },
  { code: 'site-vitrine', label: 'Site vitrine' },
  { code: 'campagnes', label: 'Campagnes' },
] as const

export function ParametresPage(): JSX.Element {
  const { slug, onglet } = useParams<{ slug: string; onglet?: string }>()
  const navigate = useNavigate()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))

  const index = Math.max(
    0,
    ONGLETS.findIndex((o) => o.code === (onglet ?? 'synchronisation')),
  )
  const courant = ONGLETS[index]!.code

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 1 }}>
        Paramètres
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
        Les réglages de {store?.name ?? 'la boutique'}.
      </Typography>

      <Tabs
        value={index}
        onChange={(_, v: number) => navigate(`/s/${slug}/parametres/${ONGLETS[v]!.code}`)}
        sx={{ mb: 4 }}
      >
        {ONGLETS.map((o) => (
          <Tab key={o.code} label={o.label} />
        ))}
      </Tabs>

      {/* La page de synchronisation existe depuis le Sprint 3 et porte son
          propre titre : on le lui retire ici, sinon l'écran en afficherait
          deux l'un sous l'autre. */}
      {courant === 'synchronisation' && <SyncSettingsPage sansEntete />}

      {courant === 'agenda' &&
        (store ? (
          <ReglagesAgenda storeId={store.storeId} />
        ) : (
          <Alert severity="warning">Boutique introuvable.</Alert>
        ))}

      {courant === 'site-vitrine' &&
        (store && slug ? (
          <EditeurSite storeId={store.storeId} slug={slug} />
        ) : (
          <Alert severity="warning">Boutique introuvable.</Alert>
        ))}

      {courant === 'campagnes' &&
        (store ? (
          <ReglagesWhatsApp storeId={store.storeId} />
        ) : (
          <Alert severity="warning">Boutique introuvable.</Alert>
        ))}
    </Box>
  )
}
